import { useAuth0 } from '#~/auth/index.js';
import type { State as DepotState } from '#~/depot/index.js';
import { useDepot } from '#~/depot/hooks.js';
import { useLinkedRoom } from '#~/room/hook_last_id.js';
import { useAgents } from '#~/agent/hooks.js';
import { broker } from '#~/agent/broker.js';
import { generateId } from '@studio/models';
import type { Agent } from '@studio/models/agent';
import type { Device } from '@studio/models/device';
import type { Room } from '@studio/models/room';
import { Status as MovementStatus } from '@studio/models/control/movement';
import type { Minimist } from '#~/device/index.js';
import { useCallback, useEffect, useRef, useState } from 'react';
import { addDevice } from '#~/video/components/add/store/utils.js';
// The panel's OWN transient-preview machinery. Reused rather than reimplemented:
// it mints the transient device id, adds the control-only device, keeps the URX
// bookkeeping that stops duplicate adds, and removes the device again. Rolling
// those steps by hand here was a third implementation of a thing the app already
// does (Aleksey, 2026-08-25).
import { syncCandidate } from '#~/video/components/add/store/mediator.js';
// ONVIF cameras do not serve video at a guessable address; the camera has to be
// asked. Same translation the production panel performs — see onvif-video.ts.
import { onvifProbeControl, onvifVideoSettings } from '../onvif-video.js';
// Per ENG-3796 P1 #3: reuse the EXISTING device-remove command (no parallel
// add/remove path) to clean up a persisted-but-never-streamed camera config.
import * as Commands from '#~/device/commands/index.js';
import { useOnDeviceAdded } from '#~/device/components/hooks.js';
import { useOnboardingEvent } from './use-onboarding-tracker.js';
import { ONBOARDING_EVENTS, type OnboardingAddressSource } from '../telemetry.js';
// Per ENG-3796 (Didier ITB #2) + knowledge onboarding-wizard-add-path-bypasses-normal-gates:
// the wizard add path skipped the plan source-limit gate the normal "Add source"
// flow enforces (footer.tsx → limitCanAddCamera). Reuse that exact gate here.
import { canAddCameraToRoom, limitCanAddCamera } from '#~/api/billing/limits.js';
import { ADD_DEVICE_TIMEOUT_MS } from '../constants.js';
import type { CameraConfig, ConnectionResult } from '../wizard/types.js';
import { logger } from '#~/logger/index.js';
import { findExistingCamera } from '../connection-checks.js';
import { buildCameraCandidate } from '../device-candidate.js';

type AgentLike = { readonly id: Agent['id'] };

// Per ENG-3796 (Didier ITB #2): sentinel-bearing message for an over-limit
// block. The connection-test screen categorizes on the "plan limit" substring
// (see screen-connection-test.tsx categorizeError) to render a dedicated
// "plan limit reached" state rather than a misleading stream-failure error.
export const PLAN_LIMIT_ERROR =
  "You've reached your plan limit. Remove a camera or upgrade your plan to add another.";

/**
 * Wraps the existing addDevice flow for the onboarding wizard. Converts
 * the user's camera form config into a Minimist candidate matching the
 * model layer's expected shape (Video.Settings = RTSP Configuration at
 * top level, Control.Settings = { protocol, host, port } at top level),
 * calls addDevice via the production Commands.add path, and watches the
 * depot for the device appearing in videoIds AND having a non-undefined
 * livekit track (via locateLiveKit).
 */
export const useBridgeAddDevice = (): {
  readonly result: ConnectionResult;
  /**
   * Add the camera. `control` is the endpoint a discovery already resolved for
   * it; omit it for a manually entered camera and the VISCA/IP default applies.
   */
  readonly add: (
    config: CameraConfig,
    control?: Minimist['control'],
    modelId?: Minimist['modelId'],
    /**
     * The Bridge that discovered this camera. Preferred over "first agent with a
     * proxy": in a studio with two Bridges only one of them can route to the
     * camera, and picking the wrong one fails the whole connection test with
     * "did not connect in time".
     */
    preferredAgentId?: string | undefined,
    /** The discovery's own video configuration, when the camera came from a scan. */
    discoveredVideo?: unknown,
    /**
     * ONVIF control endpoint for a camera that reports no video configuration.
     * The add then runs in TWO phases: probe the camera for its stream URI, then
     * add the real device with it.
     */
    onvifControl?: unknown,
    /** Origin recorded before discovered and template video share one state slot. */
    addressSource?: OnboardingAddressSource | undefined
  ) => Promise<void>;
  /**
   * Watch a device the WORKFLOW already committed, instead of adding one.
   *
   * The Add Video Source modal resolves and adds the camera itself; the
   * funnel's connecting screen only has to report on it. Adding a second time
   * from a rebuilt configuration is what dropped the control endpoint, the
   * model and the host (2026-08-28).
   */
  readonly adopt: (deviceId: Device['id']) => void;
  readonly reset: () => Promise<void>;
  // Per ENG-3796 (Didier ITB #5): mark the provisional device as KEPT. The
  // connection test persists the camera on mount so the live check can run
  // (no bridge probe RPC exists); commit() is called only when the user
  // explicitly proceeds (Save & continue / auto-advance), so the abandon
  // cleanup never removes a camera the user chose to keep.
  readonly commit: () => void;
  /**
   * Report that the caller has SEEN a decoded frame for the added device.
   *
   * The hook's own success condition and the screen's frame probe were waiting
   * on each other — the probe only ran once the add said 'connected', and the
   * add only said 'connected' once the stream was seen. A real frame is the
   * strongest evidence either of them could want, so it settles the add
   * (2026-08-25).
   */
  readonly markStreamObserved: () => void;
  // Per ENG-4261: the real control-channel health the bridge reports for
  // this device (CONTROL_STATE_OK/ERROR from the encoder's own VISCA
  // round-trip), not a client-side stub. The connection-test screen's third
  // check gates on this instead of a fixed timer.
  readonly controlStatus: MovementStatus;
  /** The bridge's video-stream error for the resolved device, if any. */
  readonly videoError: string | undefined;
} => {
  const { roomId } = useLinkedRoom();
  const agents = useAgents((a: AgentLike[]) => a);
  // The transient probe device is namespaced by app id, exactly as the panel's
  // preview devices are (video/components/add/store/mediator.tsx).
  const appId = useDepot((state: DepotState) => (state as { appId?: unknown }).appId);
  const depotRef = useRef<DepotState | undefined>(undefined);
  depotRef.current = useDepot((state: DepotState) => state);
  const { getAccessTokenSilently } = useAuth0();
  // Per ENG-3796 P1 #6: fire the same device-add analytics (track + $set_once)
  // that the normal add-source flow uses (see video/components/add/wiring.ts:173).
  const onDeviceAdded = useOnDeviceAdded();
  const emitOnboarding = useOnboardingEvent();
  const [result, setResult] = useState<ConnectionResult>({
    status: 'idle',
    deviceId: undefined,
    error: undefined
  });
  const deviceIdRef = useRef<Device['id'] | undefined>(undefined);
  // Per ENG-3796 P1 #3: the id of a config that was PERSISTED (addDevice
  // resolved) but whose stream is not yet confirmed. If the connection then
  // fails, the failure-cleanup effect removes this device. Cleared on success
  // (the camera is real) and in reset(). Undefined means "nothing to clean up"
  // (e.g. the bridge-not-ready early return, where no config was ever saved).
  const persistedDeviceIdRef = useRef<Device['id'] | undefined>(undefined);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // Per ENG-3796 P2 #4: store the resolved room ID (which may come from
  // proxy.roomId when useLinkedRoom() returns undefined) so the depot
  // selector can observe success even when the React Query hasn't resolved.
  const resolvedRoomIdRef = useRef<Room['id'] | undefined>(undefined);
  // StrictMode guard: prevent double-mount from firing add() twice.
  const addCalledRef = useRef(false);
  // Per ENG-3796 (Didier ITB #5): true once the user explicitly proceeds past
  // the connection test. Until then the persisted device is PROVISIONAL and the
  // abandon-cleanup effect removes it if the screen unmounts (Back/Skip/close).
  const committedRef = useRef(false);
  // Latest token getter, mirrored into a ref so the abandon-cleanup effect can
  // read it with an EMPTY dependency array — the cleanup must run only on true
  // unmount, never re-fire because getAccessTokenSilently changed identity
  // (which would wrongly remove the provisional device mid-flow).
  const getTokenRef = useRef(getAccessTokenSilently);
  useEffect(() => {
    getTokenRef.current = getAccessTokenSilently;
  }, [getAccessTokenSilently]);

  // Watch depot for the device being RESOLVED (present in the room's
  // devices map), not just present in videoIds. A device appears in
  // videoIds the moment its config is saved to the state-sync tree, but
  // that does not mean RTSP/LiveKit is working. The devices map is only
  // populated once the bridge has processed the device and resolved its
  // name/stream -- the same distinction the Sources list uses to show
  // "Loading..." (pending) vs the real device name (resolved). Checking
  // videoIds alone would mark a bad IP or dead camera as "connected."
  // Per ENG-3796 P1 #1: wait for resolved device, not just config save.
  const deviceInRoom = useDepot(
    (state: {
      rooms: Record<string, { videoIds: string[]; devices?: Record<string, unknown> }>;
    }) => {
      const id = deviceIdRef.current;
      if (id === undefined) return false;
      // Prefer the linked-room hook, then the room resolved by add(). This lets
      // the selector observe success before the linked-room query catches up.
      const effectiveRoomId = roomId ?? resolvedRoomIdRef.current;
      if (effectiveRoomId === undefined) return false;
      const room = state.rooms[effectiveRoomId];
      if (room === undefined) return false;
      // The device must be in videoIds AND in the devices map (resolved).
      // videoIds alone means config saved; devices[id] means the bridge
      // has processed it and the RTSP/LiveKit pipeline is live.
      return room.videoIds.includes(id) && room.devices !== undefined && id in room.devices;
    }
  );

  // Per ENG-4261: the same resolved-device record carries the bridge's
  // real control-channel status once the encoder has round-tripped a VISCA
  // command against it. Reads the RAW proto-derived field (no client-side
  // fallback logic, unlike Device.powerState) so a genuine CONTROL_STATE_OK
  // can never be confused with a guess.
  // The room's cameras, by host. Read as a snapshot at add time so onboarding
  // can recognise a camera it has ALREADY added rather than trying to add it
  // twice — see the adopt branch in add().
  const camerasInRoom = useDepot(
    (state: {
      rooms: Record<string, { devices?: Record<string, unknown> } | undefined>;
    }): readonly { readonly id: string; readonly host?: string | undefined }[] => {
      const activeRoomId = resolvedRoomIdRef.current ?? roomId;
      if (activeRoomId === undefined) return [];

      const devices = state.rooms[activeRoomId]?.devices ?? {};

      return Object.entries(devices).flatMap(([id, device]) => {
        const video = (device as { video?: { config?: { host?: unknown } } }).video;
        const host = video?.config?.host;

        return video === undefined ? [] : [{ id, ...(typeof host === 'string' ? { host } : {}) }];
      });
    }
  );
  const camerasInRoomRef = useRef(camerasInRoom);
  camerasInRoomRef.current = camerasInRoom;

  const controlStatus = useDepot((state: DepotState): MovementStatus => {
    const id = deviceIdRef.current;
    if (id === undefined) return MovementStatus.CONTROL_STATE_UNKNOWN;
    const effectiveRoomId = roomId ?? resolvedRoomIdRef.current;
    if (effectiveRoomId === undefined) return MovementStatus.CONTROL_STATE_UNKNOWN;
    const device = state.rooms[effectiveRoomId]?.devices?.[id];
    return device?.control?.movement?.status ?? MovementStatus.CONTROL_STATE_UNKNOWN;
  });

  /**
   * The bridge's own error string for this device's video stream, surfaced so
   * the connection test can tell failure CLASSES apart.
   *
   * Per ENG-3796 (2026-08-06 live run, defect 3): a wrong RTSP password and a
   * source with no publisher produced byte-identical screens — "The camera
   * connected, but no video was received. Check the camera's stream path and
   * codec." — and for the credentials case that advice is actively wrong. The
   * distinction exists at the transport (a wrong password answers `DESCRIBE
   * failed: 401`), and the depot already carries it: `video.error` is mapped
   * from the agent's stream status in protobuf/video.ts. Nothing was reading it.
   */
  const videoError = useDepot((state: DepotState): string | undefined => {
    const id = deviceIdRef.current;
    if (id === undefined) return undefined;
    const effectiveRoomId = roomId ?? resolvedRoomIdRef.current;
    if (effectiveRoomId === undefined) return undefined;
    const device = state.rooms[effectiveRoomId]?.devices?.[id];
    const error = device?.video?.error;
    return error === '' ? undefined : error;
  });

  useEffect(() => {
    if (!deviceInRoom) return;
    if (result.status !== 'connecting') return;
    // Clear the timeout since we got a positive signal.
    if (timeoutRef.current !== undefined) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
    const confirmedId = deviceIdRef.current;
    // Per ENG-3796 (Didier ITB #5): do NOT clear persistedDeviceIdRef here. The
    // stream resolved (so the failure-cleanup effect, which only fires on the
    // 'error' status, can never touch it), but the camera is still PROVISIONAL
    // until the user explicitly proceeds. Keeping the id set lets the abandon
    // cleanup remove it if the user backs out / skips / closes before committing.
    // commit() clears it so a kept camera is never removed.
    setResult({ status: 'connected', deviceId: confirmedId, error: undefined });
    // Per ENG-3796 P1 #3 + #6: fire the device-add analytics (device_add +
    // first_camera_added_at $set_once) ONLY now that the stream is confirmed.
    // Firing on config-save instead (the old behavior) marked a FAILED camera
    // as an activation, polluting the first-camera funnel metrics.
    if (confirmedId !== undefined) {
      onDeviceAdded(confirmedId);
    }
  }, [deviceInRoom, result.status, onDeviceAdded]);

  // Per ENG-3796 P1 #3: failure cleanup. When a connection attempt fails
  // AFTER the config was persisted (addDevice resolved but the stream never
  // arrived within the timeout), remove the dead config via the existing
  // remove command. This stops an unusable camera from lingering in the
  // user's Sources list and records a balancing 'removed' device_connection
  // event. Best-effort — a removal failure is logged, not surfaced, because
  // the user already sees the connection error.
  //
  // This removal is what makes the activation signal honest: queries.FindActivation
  // nets 'removed' against 'added' per device, so the balancing event written here
  // is what stops a camera that never streamed from marking the account activated
  // forever (and from burning the account for new-user testing). Without this
  // cleanup the netting has nothing to net against.
  useEffect(() => {
    if (result.status !== 'error') return;
    const persistedId = persistedDeviceIdRef.current;
    if (persistedId === undefined) return;
    // Clear first so this runs at most once per persisted device, even across
    // the re-renders the removal itself may trigger.
    persistedDeviceIdRef.current = undefined;
    void (async (): Promise<void> => {
      try {
        const token = await getAccessTokenSilently();
        // Forced: a device that never connected may not yet be confirmed in
        // the agent's control state, and remove() silently no-ops without
        // force in that case (device/commands/remove.ts's force guard) --
        // this cleanup must guarantee removal regardless.
        await Commands.remove(persistedId, token, true);
      } catch (error) {
        logger.error('Onboarding cleanup: failed to remove unusable device', { error });
      }
    })();
  }, [result.status, getAccessTokenSilently]);

  // Clean up timeout on unmount to prevent set-state-after-unmount.
  useEffect(() => {
    return () => {
      if (timeoutRef.current !== undefined) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Per ENG-3796 (Didier ITB #5): abandon cleanup. The camera is persisted on
  // mount so the live connection test can run (there is no bridge probe RPC
  // that tests without persisting — see the connection-test screen). If the
  // user leaves the connection-test screen WITHOUT committing — Back, Skip, or
  // closing the wizard, all of which unmount this hook — remove the provisional
  // device so it does not linger as an unsaved source (and so re-editing then
  // re-testing does not orphan the prior attempt as a duplicate). committed via
  // commit() clears the target so a saved camera is kept. EMPTY deps so this
  // runs only on true unmount; the token is read from a ref for the same reason.
  useEffect(() => {
    return () => {
      if (committedRef.current) return;
      const provisionalId = persistedDeviceIdRef.current;
      if (provisionalId === undefined) return;
      // Clear first so a removal can never be issued twice for the same id.
      persistedDeviceIdRef.current = undefined;
      void (async (): Promise<void> => {
        try {
          const token = await getTokenRef.current();
          // Forced: a provisional device the user never kept may never have
          // reached a control-confirmed state on the agent (e.g. the video
          // stream never decoded), and remove() silently no-ops without
          // force in that case (device/commands/remove.ts's force guard) --
          // an abandoned camera must always be discarded regardless.
          await Commands.remove(provisionalId, token, true);
        } catch (error) {
          logger.error('Onboarding cleanup: failed to remove provisional device on abandon', {
            error
          });
        }
      })();
    };
  }, []);

  const add = useCallback(
    async (
      config: CameraConfig,
      control?: Minimist['control'],
      modelId?: Minimist['modelId'],
      preferredAgentId?: string | undefined,
      discoveredVideo?: unknown,
      onvifControl?: unknown,
      addressSource?: OnboardingAddressSource | undefined
    ): Promise<void> => {
      // StrictMode guard: skip the second invocation.
      if (addCalledRef.current) {
        return;
      }
      addCalledRef.current = true;

      // Per ENG-3796 (Didier ITB #2): enforce the plan source limit before the
      // wizard commits anything. The gate is canAddCameraToRoom, not the
      // render-time limitCanAddCamera: this runs moments after a page reload,
      // when the renderer's agent mirror can still be empty, and a count of 0
      // admitted a second source into a studio that already held its one. The
      // commit gate takes the higher of the mirrored count and the room's own,
      // so neither a cold mirror nor an unwritten add can undercount.
      // Tri-state is unchanged: a loading or failed billing read still gates
      // permissively and never blocks a paying user. Check before persisting
      // anything (before the connecting/timeout setup) so a block shows no
      // connecting flash.
      // Already ours? Adopt it instead of adding it again.
      //
      // There is no cancellation on the add path, so leaving the connection test
      // mid-flight lets the add finish anyway and the camera is in the room when
      // the user comes back. Adding a second time then trips the plan source
      // limit — telling someone midway through ONBOARDING that they have too
      // many cameras, about the one camera they are being walked through adding.
      //
      // persistedDeviceIdRef is deliberately NOT set: an adopted camera was not
      // created by this attempt, so the abandon cleanup must never remove it.
      const existingId = findExistingCamera(camerasInRoomRef.current, config.host);

      if (existingId !== undefined) {
        deviceIdRef.current = existingId as Device['id'];
        setResult({ status: 'connected', deviceId: existingId as Device['id'], error: undefined });
        return;
      }

      // Same room resolution the add itself uses below: the render-time roomId
      // can be undefined while the depot catches up.
      //
      // With no room to name, fall back to the render-time gate rather than
      // skipping the check — an unresolvable room must not be MORE permissive
      // than the mirror-only gate this replaced.
      const gateRoomId = roomId ?? resolvedRoomIdRef.current;
      const allowedToAdd =
        gateRoomId === undefined
          ? limitCanAddCamera()
          : await canAddCameraToRoom(gateRoomId, await getTokenRef.current());

      if (!allowedToAdd) {
        // 'blocked', not 'error': the bridge was never contacted, so the three
        // checks have nothing to report and must not render verdicts.
        setResult({ status: 'blocked', deviceId: undefined, error: PLAN_LIMIT_ERROR });
        return;
      }

      // ENG-3796: resolve agentId at call-time, not from the closure.
      // The render-time value can go stale when the room provider's
      // useAsyncEffect re-runs (auth token refresh, Vite HMR) and
      // briefly destroys/recreates broker proxies. broker is a module
      // singleton so acquire() always reads the current proxy Map.
      // The discovering Bridge first, when it has a live proxy. Only then fall
      // back to "any agent with a proxy".
      const preferred =
        preferredAgentId === undefined
          ? undefined
          : agents.find(
              (agent: AgentLike) =>
                String(agent.id) === String(preferredAgentId) &&
                broker.acquire(agent.id) !== undefined
            )?.id;

      let resolvedAgentId =
        preferred ?? agents.find((agent: AgentLike) => broker.acquire(agent.id) !== undefined)?.id;

      // ENG-3796: brief poll — the room provider's async bootstrap
      // creates proxies asynchronously; a 1-5s gap is normal on cold
      // start or after an effect re-run.
      if (resolvedAgentId === undefined) {
        for (let attempt = 0; attempt < 5; attempt++) {
          await new Promise<void>((r) => setTimeout(r, 1_000));
          resolvedAgentId = agents.find(
            (agent: AgentLike) => broker.acquire(agent.id) !== undefined
          )?.id;
          if (resolvedAgentId !== undefined) break;
        }
      }

      // ENG-3796: roomId fallback. useLinkedRoom() depends on a React
      // Query that re-fetches when the room provider remounts. If the
      // provider crashed (TypeError in onConnected) the query returns
      // undefined even though the broker proxy already holds the roomId
      // from its creation call. Reading proxy.roomId is safe because
      // the proxy was created with the correct roomId by the provider
      // before the crash — the crash happens later in the bootstrap.
      let resolvedRoomId = roomId;
      if (resolvedRoomId === undefined && resolvedAgentId !== undefined) {
        const proxy = broker.acquire(resolvedAgentId);
        if (proxy !== undefined) {
          resolvedRoomId = proxy.roomId;
        }
      }

      if (resolvedRoomId === undefined || resolvedAgentId === undefined) {
        setResult({ status: 'error', deviceId: undefined, error: 'Bridge not ready' });
        return;
      }

      // Per ENG-3796 P2 #4: persist the resolved room ID so the depot
      // selector can read it even when useLinkedRoom() returns undefined.
      resolvedRoomIdRef.current = resolvedRoomId;

      const id = generateId<Device['id']>();
      deviceIdRef.current = id;
      setResult({ status: 'connecting', deviceId: id, error: undefined });

      // Start the timeout BEFORE the async call so a hanging addDevice
      // still surfaces an error (Codex round 4 finding #5).
      timeoutRef.current = setTimeout(() => {
        setResult((current: ConnectionResult): ConnectionResult => {
          if (current.status !== 'connecting') return current;
          return {
            status: 'error',
            deviceId: id,
            error: 'Camera did not connect in time. Check that the IP and port are correct.'
          };
        });
        timeoutRef.current = undefined;
      }, ADD_DEVICE_TIMEOUT_MS);

      try {
        const token = await getAccessTokenSilently();

        // Build the Minimist candidate matching the model layer's types.
        // Protocol enum values: 'rtsp' for video, 'visca' for VISCA/IP.
        // VISCA/IP requires headers + transport. Transport comes from the
        // user's config (defaulting to TCP for the PTZOptics-class hardware
        // this onboarding ships for) rather than being hardcoded: the previous
        // hardcoded Transport.UDP on port 52381 sent control to a dead endpoint
        // for PTZOptics cameras, so the camera never moved from the wizard even
        // though everything else connected (ENG-3796). The port likewise comes
        // from config (default 5678) and stays editable for other cameras.

        // PHASE 1 — ONVIF probe. A camera that reports no video configuration
        // has to be asked for its stream URI: the Bolin's is
        // `/media/video1&t=unicast&p=udp&ve=H265&…`, which nothing can compose.
        // A transient control-only device makes the camera answer; its
        // capabilities carry the URI. This is what the panel does, and skipping
        // it is why every ONVIF camera failed both checks.
        let resolvedVideo = discoveredVideo;

        if (resolvedVideo !== undefined) {
        } else if (onvifControl === undefined) {
        } else if (appId === undefined) {
        }

        if (resolvedVideo === undefined && onvifControl !== undefined && appId !== undefined) {
          const credentials =
            config.username.trim() === ''
              ? undefined
              : { username: config.username, password: config.password };
          // The panel's flow, driven directly: previews is the only piece of its
          // store syncCandidate touches, so a local object stands in for it. It shares
          // syncCandidate's own type so the aspect key read below is checked.
          const previews: Parameters<typeof syncCandidate>[0]['previews'] = {};
          const producer = ((fn: (draft: unknown) => void) => {
            fn({ previews } as never);
          }) as never;

          try {
            await syncCandidate({
              agentId: resolvedAgentId,
              appId: appId as never,
              candidate: { control: onvifProbeControl(onvifControl, credentials) } as never,
              previews: previews as never,
              producer,
              remove: false,
              roomId: resolvedRoomId,
              token
            });

            const probeId = previews['control']?.deviceId;

            if (probeId !== undefined) {
              const deadline = Date.now() + ADD_DEVICE_TIMEOUT_MS;
              while (resolvedVideo === undefined && Date.now() < deadline) {
                // eslint-disable-next-line no-await-in-loop
                await new Promise<void>((r) => setTimeout(r, 500));
                const probe = depotRef.current?.rooms[resolvedRoomId]?.devices?.[probeId as never];
                const caps = (probe as { control?: { capabilities?: never } } | undefined)?.control
                  ?.capabilities;
                resolvedVideo = onvifVideoSettings(caps, credentials);
              }
            }
          } catch {
          } finally {
            // Same call with remove:true — the panel's own teardown.
            try {
              await syncCandidate({
                agentId: resolvedAgentId,
                appId: appId as never,
                candidate: { control: onvifProbeControl(onvifControl, credentials) } as never,
                previews: previews as never,
                producer,
                remove: true,
                roomId: resolvedRoomId,
                token
              });
            } catch {}
          }
        }

        // Built by the extracted builder so the omission rules — control,
        // modelId, credentials — are asserted in device-candidate.test.ts
        // rather than only reachable by driving a real bridge. `video` carries
        // whatever the camera or its template reported; the builder composes
        // from the form only when nothing did.
        // WHERE the address came from, reported once per add. An ONVIF camera
        // has none until it is asked, so "the probe was needed and came back
        // empty" and "the wizard never asked" are the same screen to a user and
        // the difference to us. See ONBOARDING_EVENTS.ADDRESS_RESOLVED.
        const resolvedAddressSource: OnboardingAddressSource =
          addressSource ??
          (discoveredVideo !== undefined
            ? 'discovery'
            : resolvedVideo !== undefined
              ? onvifControl === undefined
                ? 'model_template'
                : 'onvif_probe'
              : 'form');

        // This funnel's own event — ENG-4305 has no counterpart for "where did
        // the stream address come from", which is a diagnostic about the add,
        // not a step the user took.
        emitOnboarding(ONBOARDING_EVENTS.ADDRESS_RESOLVED, 'connection-test', {
          address_source: resolvedAddressSource,
          // A probe that ran and resolved nothing is the state that cost hours
          // to identify by hand on 2026-08-25.
          onvif_probe_attempted: onvifControl !== undefined && discoveredVideo === undefined,
          resolved: resolvedVideo !== undefined
        });

        const candidate = buildCameraCandidate({
          id,
          agentId: resolvedAgentId,
          config,
          ...(control === undefined ? {} : { control }),
          ...(modelId === undefined ? {} : { modelId }),
          ...(resolvedVideo === undefined ? {} : { video: resolvedVideo })
        });

        const receipt = await addDevice(resolvedRoomId, resolvedAgentId, candidate, token);
        if (receipt?.kind !== 'saved') throw new Error('Camera save could not be confirmed.');

        // Per ENG-3796 P1 #3: the config is now persisted to the agent State,
        // but the camera is NOT yet confirmed — the bridge still has to
        // resolve the RTSP/LiveKit stream (watched by the deviceInRoom
        // selector). Record the persisted id so the failure-cleanup effect can
        // remove this device if the connection ultimately times out. The
        // device-add analytics fire only once the stream resolves (see the
        // deviceInRoom effect above), NOT here on config-save.
        persistedDeviceIdRef.current = id;
      } catch (error) {
        logger.error('Onboarding addDevice failed', { error });
        setResult({
          status: 'error',
          deviceId: undefined,
          error: error instanceof Error ? error.message : 'Failed to add camera'
        });
      }
    },
    // onDeviceAdded is intentionally NOT a dependency here: the analytics now
    // fire from the deviceInRoom success effect (ENG-3796 P1 #3), not from add().
    [roomId, agents, getAccessTokenSilently]
  );

  const adopt = useCallback((deviceId: Device['id']): void => {
    addCalledRef.current = true;
    deviceIdRef.current = deviceId;
    persistedDeviceIdRef.current = deviceId;
    setResult({ status: 'connected', deviceId, error: undefined });
  }, []);

  const reset = useCallback(async (): Promise<void> => {
    if (timeoutRef.current !== undefined) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }

    // Remove the provisional device before forgetting it.
    //
    // A SUCCESSFUL attempt deliberately keeps persistedDeviceIdRef set — the
    // camera stays provisional until the user commits — so clearing the ref
    // without removing the device leaves it in the room. The plan source limit
    // then counts it, and the very next add is refused before any bridge
    // traffic: Re-test crossed out all three checks instantly, against a camera
    // nothing had contacted.
    //
    // Awaited, not fired-and-forgotten: the caller re-adds immediately after,
    // and the limit is counted at that moment.
    const provisionalId = persistedDeviceIdRef.current;
    persistedDeviceIdRef.current = undefined;

    if (provisionalId !== undefined && !committedRef.current) {
      try {
        const token = await getTokenRef.current();
        // Forced, for the same reason the failure cleanup forces: a device that
        // never confirmed in the agent's control state is silently no-opped by
        // an unforced remove.
        await Commands.remove(provisionalId, token, true);
      } catch (error) {
        logger.error('Onboarding reset: failed to remove provisional device', { error });
      }
    }

    addCalledRef.current = false;
    committedRef.current = false;
    deviceIdRef.current = undefined;
    resolvedRoomIdRef.current = undefined;
    setResult({ status: 'idle', deviceId: undefined, error: undefined });
  }, []);

  // Per ENG-3796 (Didier ITB #5): the user explicitly kept this camera (clicked
  // Save & continue, or the success screen auto-advanced into the wizard's next
  // step). Mark it committed and clear the abandon-cleanup target so the device
  // survives the unmount that navigating forward triggers.
  const commit = useCallback(() => {
    committedRef.current = true;
    persistedDeviceIdRef.current = undefined;
  }, []);

  const markStreamObserved = useCallback(() => {
    setResult((µ) =>
      µ.status === 'connected' ? µ : { ...µ, status: 'connected', error: undefined }
    );
  }, []);

  return { result, add, adopt, reset, commit, controlStatus, videoError, markStreamObserved };
};
